<template>
    <div>
        <Head :title="__('Journal d\'activité')" />

        <!-- Filtres -->
        <div class="flex flex-col items-center justify-between gap-3 mb-4 md:flex-row">
            <search-input
                v-model="form.search"
                placeholder="Rechercher par utilisateur, action, module..."
                class="w-full max-w-md"
                @reset="reset"
            />
            <div class="flex items-center gap-3">
                <select v-model="form.limit"
                    class="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg block p-2.5">
                    <option value="10">10</option>
                    <option value="25">25</option>
                    <option value="50">50</option>
                    <option value="100">100</option>
                </select>
                <a :href="exportUrl"
                    class="uppercase gap-[1px] text-sm px-3 py-2 flex items-center justify-center border rounded">
                    <img class="w-5 h-5" src="/images/svg/export-csv.svg" alt="Export" />
                    <span>{{ __('Exporter') }}</span>
                </a>
            </div>
        </div>

        <!-- Filtres avancés -->
        <div class="flex flex-col items-center w-full gap-3 mb-4 md:flex-row">
            <div class="w-full text-sm text-gray-500 mr-2 whitespace-nowrap">{{ __('Filtrer par') }} :</div>

            <!-- Utilisateur -->
            <select-input v-model="form.user_id" class="w-full mr-2">
                <option :value="null">{{ __('Tous les utilisateurs') }}</option>
                <option v-for="u in users" :key="u.id" :value="u.id">{{ u.name }}</option>
            </select-input>

            <!-- Action -->
            <select-input v-model="form.action" class="w-full mr-2">
                <option :value="null">{{ __('Toutes les actions') }}</option>
                <option value="login">Connexion</option>
                <option value="logout">Déconnexion</option>
                <option value="login_failed">Échec connexion</option>
                <option value="create">Création</option>
                <option value="update">Modification</option>
                <option value="delete">Suppression</option>
                <option value="restore">Restauration</option>
                <option value="export">Export</option>
                <option value="import">Import</option>
            </select-input>

            <!-- Sévérité -->
            <select-input v-model="form.severity" class="w-full mr-2">
                <option :value="null">{{ __('Toute sévérité') }}</option>
                <option value="info">Info</option>
                <option value="warning">Avertissement</option>
                <option value="critical">Critique</option>
            </select-input>

            <!-- Date début -->
            <div class="w-full mr-2">
                <input
                    type="date"
                    v-model="form.date_from"
                    class="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg block w-full p-2.5"
                    :placeholder="__('Date début')"
                />
            </div>

            <!-- Date fin -->
            <div class="w-full">
                <input
                    type="date"
                    v-model="form.date_to"
                    class="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg block w-full p-2.5"
                    :placeholder="__('Date fin')"
                />
            </div>
        </div>

        <!-- Tableau -->
        <div class="overflow-x-auto bg-white rounded-md shadow">
            <table class="min-w-full whitespace-nowrap">
                <tbody>
                    <!-- En-têtes -->
                    <tr class="font-bold text-left bg-gray-50">
                        <th v-for="(h, i) in headers" :key="i" class="px-4 py-3 text-xs uppercase tracking-wider text-gray-600">
                            <span
                                :class="{ 'cursor-pointer select-none flex items-center gap-1': h.sort }"
                                @click="h.sort ? sort(h.value) : null"
                            >
                                {{ __(h.name) }}
                                <span v-if="h.sort" class="flex flex-col ml-1">
                                    <icon name="up" class="w-2 h-2"
                                        :class="form.direction === 'desc' && form.field === h.value ? 'fill-gray-800' : 'fill-gray-300'" />
                                    <icon name="down" class="w-2 h-2"
                                        :class="form.direction === 'asc' && form.field === h.value ? 'fill-gray-800' : 'fill-gray-300'" />
                                </span>
                            </span>
                        </th>
                    </tr>

                    <!-- Lignes -->
                    <tr
                        v-for="log in logs.data"
                        :key="log.id"
                        class="hover:bg-gray-50 border-t transition-colors"
                        :class="rowClass(log.severity)"
                    >
                        <!-- Horodatage -->
                        <td class="px-4 py-3 text-sm text-gray-600 whitespace-nowrap">
                            <div class="font-medium">{{ formatDate(log.created_at) }}</div>
                            <div class="text-xs text-gray-400">{{ timeAgo(log.created_at) }}</div>
                        </td>

                        <!-- Utilisateur -->
                        <td class="px-4 py-3">
                            <div class="flex items-center gap-2">
                                <div class="w-7 h-7 rounded-full bg-indigo-100 flex items-center justify-center text-indigo-600 text-xs font-bold flex-shrink-0">
                                    {{ initials(log.user_name) }}
                                </div>
                                <div>
                                    <div class="text-sm font-medium text-gray-800">{{ log.user_name ?? '-' }}</div>
                                    <div class="text-xs text-gray-400">{{ log.user_email }}</div>
                                </div>
                            </div>
                        </td>

                        <!-- Action -->
                        <td class="px-4 py-3">
                            <span class="inline-flex items-center gap-1 px-2 py-1 rounded text-xs font-semibold"
                                :class="actionClass(log.action)">
                                {{ formatAction(log.action) }}
                            </span>
                        </td>

                        <!-- Module / Ressource -->
                        <td class="px-4 py-3 text-sm text-gray-700">
                            <div class="font-medium capitalize">{{ log.subject_type ?? '-' }}</div>
                            <div v-if="log.subject_id" class="text-xs text-gray-400">#{{ log.subject_id }}</div>
                        </td>

                        <!-- Description -->
                        <td class="px-4 py-3 text-sm text-gray-600 max-w-xs">
                            <div class="truncate" :title="log.description">{{ log.description ?? '-' }}</div>
                        </td>

                        <!-- IP -->
                        <td class="px-4 py-3 text-xs text-gray-500 font-mono">
                            {{ log.ip_address ?? '-' }}
                        </td>

                        <!-- Sévérité -->
                        <td class="px-4 py-3">
                            <span class="inline-flex px-2 py-1 rounded-full text-xs font-semibold"
                                :class="severityClass(log.severity)">
                                {{ formatSeverity(log.severity) }}
                            </span>
                        </td>
                    </tr>

                    <!-- Vide -->
                    <tr v-if="logs.data.length === 0">
                        <td class="px-6 py-8 text-center text-gray-400 border-t" colspan="7">
                            <icon name="clipboard" class="w-10 h-10 mx-auto mb-2 fill-gray-200" />
                            {{ __('Aucune activité trouvée.') }}
                        </td>
                    </tr>
                </tbody>
            </table>
        </div>

        <pagination class="mt-4" :links="logs.links" />
    </div>
</template>

<script>
import { Head, Link } from '@inertiajs/vue3'
import Icon from '@/Shared/Icon'
import pickBy from 'lodash/pickBy'
import mapValues from 'lodash/mapValues'
import throttle from 'lodash/throttle'
import Layout from '@/Shared/Layout'
import Pagination from '@/Shared/Pagination'
import SelectInput from '@/Shared/SelectInput'
import SearchInput from '@/Shared/SearchInput'
import moment from 'moment'

export default {
    metaInfo: { title: 'Journal d\'activité' },

    components: { Head, Link, Icon, Pagination, SelectInput, SearchInput },

    layout: Layout,

    props: {
        logs:    Object,
        filters: Object,
        users:   Array,
    },

    remember: 'form',

    data() {
        return {
            headers: [
                { name: 'Horodatage',  value: 'created_at', sort: true  },
                { name: 'Utilisateur', value: 'user_id',    sort: true  },
                { name: 'Action',      value: 'action',     sort: true  },
                { name: 'Module',      value: 'subject_type', sort: true  },
                { name: 'Description', value: null,         sort: false },
                { name: 'Adresse IP',  value: 'ip_address', sort: false },
                { name: 'Sévérité',    value: 'severity',   sort: true  },
            ],
            form: {
                search:    this.filters.search    ?? null,
                user_id:   this.filters.user_id   ?? null,
                action:    this.filters.action    ?? null,
                severity:  this.filters.severity  ?? null,
                date_from: this.filters.date_from ?? null,
                date_to:   this.filters.date_to   ?? null,
                limit:     this.filters.limit     ?? 25,
                field:     this.filters.field     ?? 'created_at',
                direction: this.filters.direction ?? 'desc',
            },
        }
    },

    computed: {
        exportUrl() {
            const params = new URLSearchParams(pickBy(this.form))
            return `/dashboard/activity-logs/export?${params.toString()}`
        },
    },

    watch: {
        form: {
            deep: true,
            handler: throttle(function () {
                this.$inertia.get(
                    this.route('activity_logs.index'),
                    pickBy(this.form),
                    { replace: true, preserveState: true }
                )
            }, 150),
        },
    },

    methods: {
        reset() {
            this.form = mapValues(this.form, (v, k) =>
                ['limit', 'field', 'direction'].includes(k) ? this.form[k] : null
            )
        },

        sort(field) {
            this.form.field     = field
            this.form.direction = this.form.direction === 'asc' ? 'desc' : 'asc'
        },

        formatDate(date) {
            return moment(date).format('DD/MM/YYYY HH:mm:ss')
        },

        timeAgo(date) {
            return moment(date).fromNow()
        },

        initials(name) {
            if (!name) return '?'
            return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2)
        },

        formatAction(action) {
            const map = {
                login:        'Connexion',
                logout:       'Déconnexion',
                login_failed: 'Échec connexion',
                create:       'Création',
                update:       'Modification',
                delete:       'Suppression',
                restore:      'Restauration',
                export:       'Export',
                import:       'Import',
            }
            return map[action] ?? action
        },

        formatSeverity(severity) {
            const map = {
                info:     'Info',
                warning:  'Avertissement',
                critical: 'Critique',
            }
            return map[severity] ?? severity
        },

        actionClass(action) {
            const map = {
                login:        'bg-green-100 text-green-700',
                logout:       'bg-gray-100 text-gray-600',
                login_failed: 'bg-red-100 text-red-700',
                create:       'bg-blue-100 text-blue-700',
                update:       'bg-yellow-100 text-yellow-700',
                delete:       'bg-red-100 text-red-700',
                restore:      'bg-purple-100 text-purple-700',
                export:       'bg-indigo-100 text-indigo-700',
                import:       'bg-teal-100 text-teal-700',
            }
            return map[action] ?? 'bg-gray-100 text-gray-600'
        },

        severityClass(severity) {
            const map = {
                info:     'bg-blue-100 text-blue-700',
                warning:  'bg-orange-100 text-orange-700',
                critical: 'bg-red-100 text-red-700',
            }
            return map[severity] ?? 'bg-gray-100 text-gray-600'
        },

        rowClass(severity) {
            if (severity === 'critical') return 'bg-red-50'
            if (severity === 'warning')  return 'bg-orange-50'
            return ''
        },
    },

    created() {
        this.moment = moment
    },
}
</script>